/// <reference path="./primitives.d.ts" />
import { rgb } from 'pdf-lib';
import { PageGen } from '../pdf';
import { PdfPen, PdfBrush, PdfRect, PdfPoint } from '../pdf';
import { GUtil } from '../gutil';
import { GCalc } from '../gcalc';

PageGen.prototype.drawRectangle = function (
  pen: PdfPen | null,
  brush: PdfBrush | null,
  rect: PdfRect
): void {
  const pdfTopLeft = this.getXY(new PdfPoint(rect.left, rect.bottom));

  this.page.drawRectangle({
    x: pdfTopLeft.x,
    y: pdfTopLeft.y,
    width: rect.width,
    height: rect.height,
    color: brush ? brush.color.toPdfLibColor() : undefined,
    borderColor: pen ? pen.color.toPdfLibColor() : undefined,
    borderWidth: pen ? pen.thickness : undefined,
    borderDashArray: pen?.dashArray ?? undefined,
    opacity: brush ? brush.color.opacity : undefined,
    borderOpacity: pen ? pen.color.opacity : undefined,
  });
};

PageGen.prototype.drawEllipse = function (
  pen: PdfPen | null,
  brush: PdfBrush | null,
  rect: PdfRect
): void {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const center = this.getXY(new PdfPoint(cx, cy));
  const xScale = rect.width / 2;
  const yScale = rect.height / 2;

  this.page.drawEllipse({
    x: center.x,
    y: center.y,
    xScale,
    yScale,
    color: brush ? brush.color.toPdfLibColor() : undefined,
    borderColor: pen ? pen.color.toPdfLibColor() : undefined,
    borderWidth: pen ? pen.thickness : undefined,
    borderDashArray: pen?.dashArray ?? undefined,
    opacity: brush ? brush.color.opacity : undefined,
    borderOpacity: pen ? pen.color.opacity : undefined,
  });
};

PageGen.prototype.drawLine = function (pen: PdfPen, p1: PdfPoint, p2: PdfPoint): void {
  const a = this.getXY(p1);
  const b = this.getXY(p2);

  this.page.drawLine({
    start: { x: a.x, y: a.y },
    end: { x: b.x, y: b.y },
    thickness: pen.thickness ?? 1,
    color: pen.color.toPdfLibColor(),
    dashArray: pen?.dashArray ?? undefined,
    opacity: pen.color.opacity,
  });
};

PageGen.prototype.drawLines = function (pen: PdfPen, points: PdfPoint[]): void {
  if (!points || points.length < 2) return;
  const p0 = points[0];
  let path = `M ${p0.x} ${p0.y}`;
  for (let i = 1; i < points.length; i++) {
    const pp = points[i];
    path += ` L ${pp.x} ${pp.y}`;
  }

  this.page.moveTo(0, this.page.getHeight());
  this.page.drawSvgPath(path, {
    color: undefined,
    borderColor: pen ? pen.color.toPdfLibColor() : undefined,
    borderWidth: pen ? pen.thickness : undefined,
    borderDashArray: pen?.dashArray ?? undefined,
    borderOpacity: pen ? pen.color.opacity : undefined,
  });
  this.page.moveTo(0, 0);
};

PageGen.prototype.drawBeziers = function (
  pen: PdfPen | null,
  brush: PdfBrush | null,
  points: PdfPoint[]
): void {
  if (!points || points.length < 4) return;
  // Expecting [start, c1, c2, end, c1, c2, end, ...]
  const start = points[0];
  let path = `M ${start.x} ${start.y}`;
  for (let i = 1; i + 2 < points.length; i += 3) {
    const c1 = points[i];
    const c2 = points[i + 1];
    const end = points[i + 2];
    path += ` C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${end.x} ${end.y}`;
  }

  this.page.moveTo(0, this.page.getHeight());
  this.page.drawSvgPath(path, {
    color: brush ? brush.color.toPdfLibColor() : undefined,
    borderColor: pen ? pen.color.toPdfLibColor() : undefined,
    borderWidth: pen ? pen.thickness : undefined,
    borderDashArray: pen?.dashArray ?? undefined,
    opacity: brush ? brush.color.opacity : undefined,
    borderOpacity: pen ? pen.color.opacity : undefined,
  });
  this.page.moveTo(0, 0);
};

PageGen.prototype.drawArc = function (
  pen: PdfPen | null,
  brush: PdfBrush | null,
  rect: PdfRect,
  startAngleDegrees: number,
  endAngleDegrees: number
): void {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const rx = rect.width / 2;
  const ry = rect.height / 2;

  const startRad = (startAngleDegrees * Math.PI) / 180;
  const endRad = (endAngleDegrees * Math.PI) / 180;

  const startPt = { x: cx + rx * Math.cos(startRad), y: cy + ry * Math.sin(startRad) };
  const endPt = { x: cx + rx * Math.cos(endRad), y: cy + ry * Math.sin(endRad) };

  const diff = endAngleDegrees - startAngleDegrees;
  const sweepFlag = diff >= 0 ? 1 : 0;
  const delta = ((diff % 360) + 360) % 360;
  const largeArcFlag = sweepFlag === 1 ? (delta > 180 ? 1 : 0) : delta < 180 ? 1 : 0;

  const path = `M ${startPt.x} ${startPt.y} A ${rx} ${ry} 0 ${largeArcFlag} ${sweepFlag} ${endPt.x} ${endPt.y}`;

  this.page.moveTo(0, this.page.getHeight());
  this.page.drawSvgPath(path, {
    color: brush ? brush.color.toPdfLibColor() : undefined,
    borderColor: pen ? pen.color.toPdfLibColor() : undefined,
    borderWidth: pen ? pen.thickness : undefined,
    borderDashArray: pen?.dashArray ?? undefined,
    opacity: brush ? brush.color.opacity : undefined,
    borderOpacity: pen ? pen.color.opacity : undefined,
  });
  this.page.moveTo(0, 0);
};

PageGen.prototype.drawRoundedRectangle = function (
  pen: PdfPen | null,
  brush: PdfBrush | null,
  rect: PdfRect,
  radius: number
): void {
  const x = rect.left;
  const yTop = rect.top;
  const x2 = rect.right;
  const yBottom = rect.bottom;

  // Points in top-left coords, convert each
  const p1 = { x: x + radius, y: yTop };
  const p2 = { x: x2 - radius, y: yTop };
  const p3 = { x: x2, y: yTop + radius };
  const p4 = { x: x2, y: yBottom - radius };
  const p5 = { x: x2 - radius, y: yBottom };
  const p6 = { x: x + radius, y: yBottom };
  const p7 = { x, y: yBottom - radius };
  const p8 = { x, y: yTop + radius };

  // Use arc commands for the corners (rx=ry=radius)
  const path = [
    `M ${p1.x} ${p1.y}`,
    `L ${p2.x} ${p2.y}`,
    `A ${radius} ${radius} 0 0 1 ${p3.x} ${p3.y}`,
    `L ${p4.x} ${p4.y}`,
    `A ${radius} ${radius} 0 0 1 ${p5.x} ${p5.y}`,
    `L ${p6.x} ${p6.y}`,
    `A ${radius} ${radius} 0 0 1 ${p7.x} ${p7.y}`,
    `L ${p8.x} ${p8.y}`,
    `A ${radius} ${radius} 0 0 1 ${p1.x} ${p1.y}`,
    `Z`,
  ].join(' ');

  this.page.moveTo(0, this.page.getHeight());
  this.page.drawSvgPath(path, {
    color: brush ? brush.color.toPdfLibColor() : undefined,
    borderColor: pen ? pen.color.toPdfLibColor() : undefined,
    borderWidth: pen ? pen.thickness : undefined,
    borderDashArray: pen?.dashArray ?? undefined,
    opacity: brush ? brush.color.opacity : undefined,
    borderOpacity: pen ? pen.color.opacity : undefined,
  });
  this.page.moveTo(0, 0);
};

PageGen.prototype.drawPolygon = function (
  pen: PdfPen | null,
  brush: PdfBrush | null,
  points: PdfPoint[]
): void {
  if (!points || points.length < 3) return;

  // Build SVG path in TOP-LEFT space (no Y flipping!)
  const p0 = points[0];
  let path = `M ${p0.x} ${p0.y}`;

  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    path += ` L ${p.x} ${p.y}`;
  }

  path += ' Z';

  this.page.moveTo(0, this.page.getHeight());
  this.page.drawSvgPath(path, {
    color: brush ? brush.color.toPdfLibColor() : undefined,
    borderColor: pen ? pen.color.toPdfLibColor() : undefined,
    borderWidth: pen ? pen.thickness : undefined,
    borderDashArray: pen?.dashArray ?? undefined,
    opacity: brush ? brush.color.opacity : undefined,
    borderOpacity: pen ? pen.color.opacity : undefined,
  });
  this.page.moveTo(0, 0);
};

PageGen.prototype.drawPie = function (
  pen: PdfPen | null,
  brush: PdfBrush | null,
  rect: PdfRect,
  startAngleDegrees: number,
  endAngleDegrees: number
): void {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const rx = rect.width / 2;
  const ry = rect.height / 2;

  const startRad = (startAngleDegrees * Math.PI) / 180;
  const endRad = (endAngleDegrees * Math.PI) / 180;

  const startPt = { x: cx + rx * Math.cos(startRad), y: cy + ry * Math.sin(startRad) };
  const endPt = { x: cx + rx * Math.cos(endRad), y: cy + ry * Math.sin(endRad) };

  const delta = (((endAngleDegrees - startAngleDegrees) % 360) + 360) % 360;
  const largeArcFlag = delta > 180 ? 1 : 0;
  const sweepFlag = endAngleDegrees - startAngleDegrees >= 0 ? 1 : 0;

  const center = { x: cx, y: cy };

  const path = `M ${center.x} ${center.y} L ${startPt.x} ${startPt.y} A ${rx} ${ry} 0 ${largeArcFlag} ${sweepFlag} ${endPt.x} ${endPt.y} Z`;

  this.page.moveTo(0, this.page.getHeight());
  this.page.drawSvgPath(path, {
    color: brush ? brush.color.toPdfLibColor() : undefined,
    borderColor: pen ? pen.color.toPdfLibColor() : undefined,
    borderWidth: pen ? pen.thickness : undefined,
    borderDashArray: pen?.dashArray ?? undefined,
    opacity: brush ? brush.color.opacity : undefined,
    borderOpacity: pen ? pen.color.opacity : undefined,
  });
  this.page.moveTo(0, 0);
};

PageGen.prototype.drawAnnularSector = function (
  pen: PdfPen | null,
  brush: PdfBrush | null,
  rect: PdfRect,
  innerRadiusRatio: number,
  startAngleDegrees: number,
  endAngleDegrees: number
): void {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const rx = rect.width / 2;
  const ry = rect.height / 2;
  const innerRx = rx * innerRadiusRatio;
  const innerRy = ry * innerRadiusRatio;

  const startRad = (startAngleDegrees * Math.PI) / 180;
  const endRad = (endAngleDegrees * Math.PI) / 180;

  // Outer arc points
  const outerStartPt = { x: cx + rx * Math.cos(startRad), y: cy + ry * Math.sin(startRad) };
  const outerEndPt = { x: cx + rx * Math.cos(endRad), y: cy + ry * Math.sin(endRad) };

  // Inner arc points
  const innerStartPt = { x: cx + innerRx * Math.cos(endRad), y: cy + innerRy * Math.sin(endRad) };
  const innerEndPt = { x: cx + innerRx * Math.cos(startRad), y: cy + innerRy * Math.sin(startRad) };

  const delta = (((endAngleDegrees - startAngleDegrees) % 360) + 360) % 360;
  const largeArcFlag = delta > 180 ? 1 : 0;
  const sweepFlag = endAngleDegrees - startAngleDegrees >= 0 ? 1 : 0;

  // Create annular sector path: outer arc from start to end, then line to inner end, then inner arc back
  const path =
    `M ${outerStartPt.x} ${outerStartPt.y} ` +
    `A ${rx} ${ry} 0 ${largeArcFlag} ${sweepFlag} ${outerEndPt.x} ${outerEndPt.y} ` +
    `L ${innerStartPt.x} ${innerStartPt.y} ` +
    `A ${innerRx} ${innerRy} 0 ${largeArcFlag} ${1 - sweepFlag} ${innerEndPt.x} ${innerEndPt.y} ` +
    'Z';

  this.page.moveTo(0, this.page.getHeight());
  this.page.drawSvgPath(path, {
    color: brush ? brush.color.toPdfLibColor() : undefined,
    borderColor: pen ? pen.color.toPdfLibColor() : undefined,
    borderWidth: pen ? pen.thickness : undefined,
    borderDashArray: pen?.dashArray ?? undefined,
    opacity: brush ? brush.color.opacity : undefined,
    borderOpacity: pen ? pen.color.opacity : undefined,
  });
  this.page.moveTo(0, 0);
};

PageGen.prototype.drawArcBetweenPoints = function (
  pen: PdfPen,
  center: PdfPoint,
  p1: PdfPoint,
  p2: PdfPoint,
  clockwise = false
): void {
  const radius = Math.hypot(p1.x - center.x, p1.y - center.y);
  const rectX = center.x - radius;
  const rectY = center.y - radius;
  const diameter = 2 * radius;
  const rect = { x: rectX, y: rectY, width: diameter, height: diameter } as PdfRect;

  let startAngle = (Math.atan2(p1.y - center.y, p1.x - center.x) * 180) / Math.PI;
  let endAngle = (Math.atan2(p2.y - center.y, p2.x - center.x) * 180) / Math.PI;

  startAngle = GCalc.normalizeAngle(startAngle);
  endAngle = GCalc.normalizeAngle(endAngle);

  if (clockwise) {
    if (endAngle <= startAngle) endAngle += 360;
  } else {
    if (endAngle >= startAngle) endAngle -= 360;
  }

  this.drawArc(pen, null, rect, startAngle, endAngle);
};
